import { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { useUsers } from '@/hooks/use-users';
import { UserTableHeader } from './UserTableHeader';
import { UserTable } from './UserTable';
import { NewUser } from './components/NewUser';
import { EditUserModal } from './EditUserModal';
import { BulkUserImport } from './components/BulkUserImport';
import { InviteSuccess } from './components/NewUser/components/InviteSuccess';
import { Pagination, PAGINATION_PAGE_SIZE } from '@/components/Pagination';
import { userService } from '@/services/user';
import { UserListItem } from '@/types/user';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth';
import { getLinkedPartners, isBrandScopedUser } from '@/utils/rbac';
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog';

export default function Account() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const scopedPartnerIds = isBrandScopedUser(currentUser)
    ? getLinkedPartners(currentUser).map((partner) => partner.id)
    : [];
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [type, setType] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const [status, setStatus] = useState('');
  const [partnerIds, setPartnerIds] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<'asc' | 'desc' | undefined>(undefined);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserListItem | null>(null);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCurrentPage(1);
  }, [type, roles, status, partnerIds, sortBy, order]);

  const effectivePartnerIds =
    partnerIds.length > 0
      ? partnerIds
      : scopedPartnerIds.length > 0
        ? scopedPartnerIds
        : undefined;

  const { users, total, isLoading, error, refetch, updateUserStatus } = useUsers({
    offset: (currentPage - 1) * PAGINATION_PAGE_SIZE,
    limit: PAGINATION_PAGE_SIZE,
    q: debouncedSearchTerm || undefined,
    type: type || undefined,
    roles: roles.length ? roles : undefined,
    status: status || undefined,
    partner_ids: effectivePartnerIds,
    sort_by: sortBy,
    order,
  });

  const handleSort = (column: 'name' | 'whatsapp' | 'type' | 'role' | 'status' | 'created_at') => {
    if (sortBy !== column) {
      setSortBy(column);
      setOrder('asc');
      return;
    }
    if (order === 'asc') {
      setOrder('desc');
      return;
    }
    setSortBy(undefined);
    setOrder(undefined);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setType('');
    setRoles([]);
    setStatus('');
    setPartnerIds([]);
    setCurrentPage(1);
  };

  const handleNewUser = () => {
    setIsAddModalOpen(true);
  };

  const handleAddModalClose = () => {
    setIsAddModalOpen(false);
  };

  const handleUserCreated = () => {
    refetch();
  };

  const handleEdit = (user: UserListItem) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setSelectedUser(null);
  };

  const handleUserUpdated = () => {
    refetch();
  };

  const handleBlock = async (user: UserListItem) => {
    try {
      await userService.blockUser(user.id);
      updateUserStatus(user.id, 'BLOCKED');
      toast({
        title: 'Usuário bloqueado com sucesso!',
        description: `${user.name || user.email} foi bloqueado e não poderá mais acessar o sistema`,
      });
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao bloquear usuário';
      toast({
        variant: 'error',
        title: 'Erro ao bloquear usuário',
        description: errorMessage,
      });
    }
  };

  const handleUnblock = async (user: UserListItem) => {
    try {
      await userService.unblockUser(user.id);
      updateUserStatus(user.id, 'ACTIVE');
      toast({
        title: 'Usuário desbloqueado com sucesso!',
        description: `${user.name || user.email} foi desbloqueado e pode acessar o sistema novamente`,
      });
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao desbloquear usuário';
      toast({
        variant: 'error',
        title: 'Erro ao desbloquear usuário',
        description: errorMessage,
      });
    }
  };

  return (
    <div className="w-full min-w-0">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </button>
      <UserTableHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onNewUser={handleNewUser}
        onImportCsv={() => setIsBulkImportOpen(true)}
        type={type}
        roles={roles}
        status={status}
        partnerIds={partnerIds}
        onTypeChange={setType}
        onRolesChange={setRoles}
        onStatusChange={setStatus}
        onPartnerIdsChange={setPartnerIds}
        onClearFilters={handleClearFilters}
      />
      <div className="mb-6 mt-6 w-full min-w-0">
        <UserTable
          users={users}
          isLoading={isLoading}
          error={error}
          sortBy={sortBy}
          order={order}
          onSort={handleSort}
          onEdit={handleEdit}
          onBlock={handleBlock}
          onUnblock={handleUnblock}
          onInviteGenerated={setInviteUrl}
        />
      </div>

      {!isLoading && (
        <Pagination
          currentPage={currentPage}
          totalItems={total}
          onPageChange={setCurrentPage}
        />
      )}
      <NewUser
        isOpen={isAddModalOpen}
        onClose={handleAddModalClose}
        onSuccess={handleUserCreated}
      />
      <BulkUserImport
        isOpen={isBulkImportOpen}
        onClose={() => setIsBulkImportOpen(false)}
        onSuccess={handleUserCreated}
      />
      <EditUserModal
        isOpen={isEditModalOpen}
        onClose={handleEditModalClose}
        onSuccess={handleUserUpdated}
        user={selectedUser}
      />
      <Dialog open={Boolean(inviteUrl)} onOpenChange={(open) => (!open ? setInviteUrl(null) : undefined)}>
        <DialogPortal>
          <DialogOverlay className="bg-black/40 backdrop-blur-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:duration-300 data-[state=closed]:duration-200" />
          <DialogPrimitive.Content className="fixed inset-y-0 right-0 z-50 flex h-[100dvh] w-full max-w-[min(420px,100vw)] min-w-0 flex-col overflow-hidden border-l bg-white shadow-xl outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-right-full data-[state=closed]:slide-out-to-right-full data-[state=open]:duration-300 data-[state=closed]:duration-300 data-[state=open]:ease-out data-[state=closed]:ease-in">
            <div className="flex shrink-0 items-center justify-between px-6 py-4">
              <DialogTitle className="text-lg font-semibold text-gray-900">Link de convite</DialogTitle>
              <button
                type="button"
                onClick={() => setInviteUrl(null)}
                className="rounded-sm p-1 text-gray-700 opacity-70 hover:opacity-100"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" strokeWidth={2.5} />
              </button>
            </div>
            {inviteUrl ? (
              <InviteSuccess
                inviteUrl={inviteUrl}
                title="Novo link de convite gerado!"
                description="Copie o novo link de convite abaixo e envie para o usuário criar a senha de acesso."
                onFinish={() => setInviteUrl(null)}
              />
            ) : null}
          </DialogPrimitive.Content>
        </DialogPortal>
      </Dialog>
    </div>
  );
}
